import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Label,
  Pagination,
  PaginationItem,
  PaginationLink,
  Row,
  Spinner,
} from "reactstrap";

import BreadCrumb from "../../../../Components/Common/BreadCrumb";
import TableContainer from "../../../../Components/Common/TableContainer";
import {
  getWebhookLog,
  getWebhookLogs,
  WebhookLog,
} from "../../../../services/superAdmin/adminWebhookLogService";
import { PaginationMeta } from "../../../../types/api";
import WebhookLogDetailModal from "./WebhookLogDetailModal";

const PER_PAGE = 15;
const FILTER_DEBOUNCE_MS = 300;

const resolveHttpStatusBadgeColor = (status: number | null): string => {
  if (status === null) {
    return "secondary";
  }

  if (status >= 200 && status < 300) {
    return "success";
  }

  if (status >= 400 && status < 500) {
    return "warning";
  }

  if (status >= 500) {
    return "danger";
  }

  return "info";
};

const SuperAdminWebhookLogs = () => {
  const { t, i18n } = useTranslation();

  const [logs, setLogs] = useState<WebhookLog[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [page, setPage] = useState(1);
  const [providerFilter, setProviderFilter] = useState("");
  const [directionFilter, setDirectionFilter] = useState("");
  const [tenantIdFilter, setTenantIdFilter] = useState("");
  const [httpStatusFilter, setHttpStatusFilter] = useState("");
  const [debouncedProviderFilter, setDebouncedProviderFilter] = useState("");
  const [debouncedTenantIdFilter, setDebouncedTenantIdFilter] = useState("");
  const [debouncedHttpStatusFilter, setDebouncedHttpStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState<WebhookLog | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  const notAvailable = t("admin.webhookLogs.common.notAvailable");

  useEffect(() => {
    document.title = t("admin.webhookLogs.pageTitle");
  }, [t, i18n.language]);

  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedProviderFilter(providerFilter.trim()),
      FILTER_DEBOUNCE_MS
    );

    return () => clearTimeout(timer);
  }, [providerFilter]);

  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedTenantIdFilter(tenantIdFilter.trim()),
      FILTER_DEBOUNCE_MS
    );

    return () => clearTimeout(timer);
  }, [tenantIdFilter]);

  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedHttpStatusFilter(httpStatusFilter.trim()),
      FILTER_DEBOUNCE_MS
    );

    return () => clearTimeout(timer);
  }, [httpStatusFilter]);

  useEffect(() => {
    setPage(1);
  }, [
    debouncedProviderFilter,
    directionFilter,
    debouncedTenantIdFilter,
    debouncedHttpStatusFilter,
  ]);

  const formatDateTime = useCallback(
    (value: string) =>
      new Intl.DateTimeFormat(i18n.language, {
        dateStyle: "short",
        timeStyle: "short",
      }).format(new Date(value)),
    [i18n.language]
  );

  const formatTenantLabel = useCallback(
    (log: WebhookLog) => {
      if (log.tenant) {
        return `#${log.tenant.id} — ${log.tenant.name}`;
      }

      if (log.tenant_id) {
        return `#${log.tenant_id}`;
      }

      return notAvailable;
    },
    [notAvailable]
  );

  const fetchLogs = useCallback(async () => {
    setLoading(true);

    const parsedTenantId = debouncedTenantIdFilter
      ? Number.parseInt(debouncedTenantIdFilter, 10)
      : undefined;
    const parsedHttpStatus = debouncedHttpStatusFilter
      ? Number.parseInt(debouncedHttpStatusFilter, 10)
      : undefined;

    try {
      const response = await getWebhookLogs({
        page,
        per_page: PER_PAGE,
        ...(debouncedProviderFilter
          ? { provider_slug: debouncedProviderFilter }
          : {}),
        ...(directionFilter
          ? { direction: directionFilter as "inbound" | "outbound" }
          : {}),
        ...(parsedTenantId && !Number.isNaN(parsedTenantId)
          ? { tenant_id: parsedTenantId }
          : {}),
        ...(parsedHttpStatus && !Number.isNaN(parsedHttpStatus)
          ? { http_status: parsedHttpStatus }
          : {}),
      });

      setLogs(response.data);
      setMeta(response.meta);
    } catch (error: any) {
      toast.error(error?.data || t("admin.webhookLogs.toast.loadError"));
    } finally {
      setLoading(false);
    }
  }, [
    page,
    debouncedProviderFilter,
    directionFilter,
    debouncedTenantIdFilter,
    debouncedHttpStatusFilter,
    t,
  ]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const openDetails = useCallback(async (log: WebhookLog) => {
    setSelectedLog(log);
    setDetailModalOpen(true);
    setDetailLoading(true);

    try {
      const response = await getWebhookLog(log.id);
      setSelectedLog(response.data);
    } catch (error: any) {
      toast.error(error?.data || t("admin.webhookLogs.toast.detailError"));
      setDetailModalOpen(false);
      setSelectedLog(null);
    } finally {
      setDetailLoading(false);
    }
  }, [t]);

  const closeDetails = () => {
    setDetailModalOpen(false);
    setSelectedLog(null);
    setDetailLoading(false);
  };

  const columns = useMemo(
    () => [
      {
        header: t("admin.webhookLogs.table.date"),
        accessorKey: "created_at",
        enableColumnFilter: false,
        cell: (cell: any) => formatDateTime(cell.getValue()),
      },
      {
        header: t("admin.webhookLogs.table.tenant"),
        accessorKey: "tenant",
        enableColumnFilter: false,
        cell: (cell: any) => formatTenantLabel(cell.row.original as WebhookLog),
      },
      {
        header: t("admin.webhookLogs.table.provider"),
        accessorKey: "provider_slug",
        enableColumnFilter: false,
        cell: (cell: any) => (
          <span className="font-monospace">{cell.getValue()}</span>
        ),
      },
      {
        header: t("admin.webhookLogs.table.direction"),
        accessorKey: "direction",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const direction = cell.getValue() as string;

          return (
            <Badge
              color={direction === "inbound" ? "soft-info" : "soft-warning"}
              className="fs-12"
            >
              {t(`admin.webhookLogs.direction.${direction}`, {
                defaultValue: direction,
              })}
            </Badge>
          );
        },
      },
      {
        header: t("admin.webhookLogs.table.event"),
        accessorKey: "event",
        enableColumnFilter: false,
        cell: (cell: any) => (
          <span className="font-monospace">{cell.getValue()}</span>
        ),
      },
      {
        header: t("admin.webhookLogs.table.httpStatus"),
        accessorKey: "http_status",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const status = cell.getValue() as number | null;

          if (status === null) {
            return notAvailable;
          }

          return (
            <Badge color={resolveHttpStatusBadgeColor(status)} className="fs-12">
              {status}
            </Badge>
          );
        },
      },
      {
        header: t("admin.webhookLogs.table.actions"),
        accessorKey: "id",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const log = cell.row.original as WebhookLog;

          return (
            <Button
              color="soft-primary"
              size="sm"
              onClick={() => openDetails(log)}
            >
              <i className="ri-eye-line align-bottom me-1" />
              {t("admin.webhookLogs.actions.details")}
            </Button>
          );
        },
      },
    ],
    [t, formatDateTime, formatTenantLabel, notAvailable, openDetails]
  );

  const totalPages = meta?.last_page ?? 1;

  return (
    <React.Fragment>
      <ToastContainer closeButton pauseOnHover />
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={t("admin.webhookLogs.breadcrumb.title")}
            pageTitle={t("admin.webhookLogs.breadcrumb.pageTitle")}
          />

          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader className="border-0">
                  <Row className="align-items-center g-3">
                    <Col md={4}>
                      <h4 className="card-title mb-0">
                        {t("admin.webhookLogs.card.title")}
                      </h4>
                    </Col>
                    <Col md={8}>
                      <div className="d-flex flex-column flex-lg-row gap-2 justify-content-lg-end">
                        <div className="flex-grow-1 flex-lg-grow-0" style={{ minWidth: 160 }}>
                          <Label htmlFor="webhook-log-provider-filter" className="visually-hidden">
                            {t("admin.webhookLogs.filters.provider.label")}
                          </Label>
                          <Input
                            id="webhook-log-provider-filter"
                            type="text"
                            value={providerFilter}
                            placeholder={t("admin.webhookLogs.filters.provider.placeholder")}
                            onChange={(event) => setProviderFilter(event.target.value)}
                          />
                        </div>
                        <div className="flex-grow-1 flex-lg-grow-0" style={{ minWidth: 140 }}>
                          <Label htmlFor="webhook-log-direction-filter" className="visually-hidden">
                            {t("admin.webhookLogs.filters.direction.label")}
                          </Label>
                          <Input
                            id="webhook-log-direction-filter"
                            type="select"
                            value={directionFilter}
                            onChange={(event) => setDirectionFilter(event.target.value)}
                          >
                            <option value="">
                              {t("admin.webhookLogs.filters.direction.all")}
                            </option>
                            <option value="inbound">
                              {t("admin.webhookLogs.direction.inbound")}
                            </option>
                            <option value="outbound">
                              {t("admin.webhookLogs.direction.outbound")}
                            </option>
                          </Input>
                        </div>
                        <div className="flex-grow-1 flex-lg-grow-0" style={{ minWidth: 120 }}>
                          <Label htmlFor="webhook-log-tenant-filter" className="visually-hidden">
                            {t("admin.webhookLogs.filters.tenantId.label")}
                          </Label>
                          <Input
                            id="webhook-log-tenant-filter"
                            type="text"
                            inputMode="numeric"
                            value={tenantIdFilter}
                            placeholder={t("admin.webhookLogs.filters.tenantId.placeholder")}
                            onChange={(event) => setTenantIdFilter(event.target.value)}
                          />
                        </div>
                        <div className="flex-grow-1 flex-lg-grow-0" style={{ minWidth: 120 }}>
                          <Label htmlFor="webhook-log-status-filter" className="visually-hidden">
                            {t("admin.webhookLogs.filters.httpStatus.label")}
                          </Label>
                          <Input
                            id="webhook-log-status-filter"
                            type="text"
                            inputMode="numeric"
                            value={httpStatusFilter}
                            placeholder={t("admin.webhookLogs.filters.httpStatus.placeholder")}
                            onChange={(event) => setHttpStatusFilter(event.target.value)}
                          />
                        </div>
                      </div>
                    </Col>
                  </Row>
                </CardHeader>

                <CardBody>
                  {loading ? (
                    <div className="d-flex justify-content-center py-5">
                      <Spinner color="primary" />
                    </div>
                  ) : logs.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                      <p className="mb-0">{t("admin.webhookLogs.empty.noData")}</p>
                    </div>
                  ) : (
                    <>
                      <div className="webhook-logs-table">
                        <style>
                          {`
                            .webhook-logs-table .row.align-items-center.mt-2 {
                              display: none !important;
                            }
                            .webhook-log-json {
                              max-height: 320px;
                              overflow: auto;
                              white-space: pre-wrap;
                              word-break: break-word;
                            }
                          `}
                        </style>
                        <TableContainer
                          columns={columns}
                          data={logs}
                          customPageSize={logs.length || PER_PAGE}
                          tableClass="align-middle table-nowrap mb-0"
                          theadClass="table-light"
                        />
                      </div>

                      {meta && totalPages > 1 && (
                        <Row className="align-items-center mt-3 g-3">
                          <Col sm={6}>
                            <p className="text-muted mb-0">
                              {t("admin.webhookLogs.pagination.showing", {
                                from: meta.from ?? 0,
                                to: meta.to ?? 0,
                                total: meta.total,
                              })}
                            </p>
                          </Col>
                          <Col sm={6}>
                            <Pagination className="pagination-separated justify-content-sm-end mb-0">
                              <PaginationItem disabled={page <= 1}>
                                <PaginationLink
                                  href="#"
                                  onClick={(event) => {
                                    event.preventDefault();
                                    if (page > 1) {
                                      setPage(page - 1);
                                    }
                                  }}
                                >
                                  {t("admin.webhookLogs.pagination.previous")}
                                </PaginationLink>
                              </PaginationItem>
                              <PaginationItem active>
                                <PaginationLink href="#">
                                  {page} / {totalPages}
                                </PaginationLink>
                              </PaginationItem>
                              <PaginationItem disabled={page >= totalPages}>
                                <PaginationLink
                                  href="#"
                                  onClick={(event) => {
                                    event.preventDefault();
                                    if (page < totalPages) {
                                      setPage(page + 1);
                                    }
                                  }}
                                >
                                  {t("admin.webhookLogs.pagination.next")}
                                </PaginationLink>
                              </PaginationItem>
                            </Pagination>
                          </Col>
                        </Row>
                      )}
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <WebhookLogDetailModal
        isOpen={detailModalOpen}
        log={selectedLog}
        loading={detailLoading}
        onClose={closeDetails}
        formatDateTime={formatDateTime}
      />
    </React.Fragment>
  );
};

export default SuperAdminWebhookLogs;
